/**
 * React Query hook for the folder rail's model (plan N2).
 *
 * ONE request per lane, not seven browser-side counts. The backend
 * (`services/mail_folders.py`) computes every count in one pass and caches it
 * per (org, user, inbox), so the rail can re-render freely.
 *
 * Modelled on `useUnifiedInbox.js` — `lib/api`'s `apiFetch`, `useAuth()`'s
 * `getAuthHeader()`, and a named key factory — and deliberately NOT on
 * `hooks/useInbox.js`, which builds its own `${process.env.REACT_APP_BACKEND_URL}/api`
 * base with no `|| ''` fallback and therefore requests `undefined/api/...` in
 * the same-origin deployment this runtime actually ships.
 *
 * ## The one contract that matters downstream
 *
 * `count: null` is NOT zero. A zero is a folder that was counted and is empty;
 * a null is a folder this backend does not count — Drafts, Important and
 * Scheduled, none of which have a data source in this repo. The rail must
 * render **no badge** for null and a literal `0` for zero, so this hook passes
 * counts through untouched and never coalesces a null to 0.
 *
 * Sent is NOT among them: it reported null until the audit, but `direction` is
 * stamped by every ingest and send path, so it is now counted like any other.
 */
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
// Defined in a leaf module and re-exported here, so a caller that only needs to
// INVALIDATE the rail does not drag `AuthContext` (and its module-scope
// `axios.create()`) in behind it. Still one definition — see
// `hooks/mailFolderKeys.js`.
import { mailFolderKeys } from './mailFolderKeys';

export { mailFolderKeys };

/**
 * A stable empty model, so a pending/failed query does not hand the rail a new
 * object identity on every render and re-run its memos for nothing.
 */
export const EMPTY_FOLDER_MODEL = Object.freeze({
    system: Object.freeze([]),
    groups: Object.freeze([]),
    labels: Object.freeze([]),
});

/**
 * The folder rail's system views, groups and labels for one lane.
 *
 * @param {string|null} inboxId  A unified inbox id, or null/undefined for the
 *                               personal lane (the parameter is simply omitted
 *                               from the request, which is what the endpoint
 *                               reads as "personal").
 * @param {object}  [options]
 * @param {boolean} [options.enabled]  Force the query off — used when a caller
 *                               already has a model and only wants the rail.
 */
export function useMailFolders(inboxId = null, { enabled = true } = {}) {
    const { getAuthHeader, user } = useAuth();
    return useQuery({
        queryKey: mailFolderKeys.lane(inboxId),
        enabled: Boolean(user) && enabled !== false,
        queryFn: () => {
            const query = inboxId
                ? `?${new URLSearchParams({ inbox_id: inboxId }).toString()}`
                : '';
            return apiFetch(`/mail/folders${query}`, getAuthHeader());
        },
        // The envelope is `{ ok, data }`; the rail wants `data`. Counts inside
        // are passed through exactly as the server sent them — see the null-vs-
        // zero note above.
        select: response => response?.data || EMPTY_FOLDER_MODEL,
        // No retry: the expected failures are a revoked grant (403) and the
        // feature being off (404), and neither improves on a second attempt.
        retry: false,
        // The server already caches counts for 30s; matching it here keeps a
        // mailbox switch from firing a request the server would answer from its
        // own cache anyway.
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
    });
}

export default useMailFolders;
